import React from 'react';
import styles from './DataComplianceWarning.module.scss';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import WarningIcon from '@/assets/Icons/Warning.svg?react';
import routes from '@/constants/routes.ts';
import { useNavigate } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getUncompliantTripsNum } from '@/api/dataCompliance.ts';
import breakpoints from '@/constants/breakpoints.ts';
import { trackEvent, trackPostHogEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';
import {
	actionNeededDefaultPeriod,
	actionNeededReportingPeriod,
	dataCompliancePeriodLabels,
} from '@/constants/dataCompliance.ts';

const DataComplianceWarning: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const isMissingDetailsMode = searchParams.get('mode') === 'missing-details';

	const last30DaysTripsQuery = useQuery({
		queryKey: [queryKeys.dataComplianceNumOfTrips, actionNeededDefaultPeriod],
		queryFn: () => getUncompliantTripsNum({ period: actionNeededDefaultPeriod }),
	});
	const reportingPeriodTripsQuery = useQuery({
		queryKey: [queryKeys.dataComplianceNumOfTrips, actionNeededReportingPeriod],
		queryFn: () => getUncompliantTripsNum({ period: actionNeededReportingPeriod }),
		enabled: last30DaysTripsQuery.data?.count === 0,
	});
	const last30DaysTripCount = last30DaysTripsQuery.data?.count ?? 0;
	const reportingPeriodTripCount = reportingPeriodTripsQuery.data?.count ?? 0;
	const hasLast30DaysTrips = last30DaysTripCount > 0;
	const hasReportingPeriodTrips =
		last30DaysTripsQuery.isSuccess &&
		!last30DaysTripsQuery.isFetching &&
		last30DaysTripCount === 0 &&
		reportingPeriodTripCount > 0;
	const tripCount = hasLast30DaysTrips ? last30DaysTripCount : hasReportingPeriodTrips ? reportingPeriodTripCount : 0;
	const reviewPeriod = hasLast30DaysTrips ? actionNeededDefaultPeriod : actionNeededReportingPeriod;
	const last30DaysPeriodLabel = dataCompliancePeriodLabels[actionNeededDefaultPeriod].toLocaleLowerCase();

	if (!tripCount) {
		return null;
	}

	return (
		<FlexBlock
			justifyContent="space-between"
			alignItems="center"
			flexDirection={{ [breakpoints.zero]: 'column', [breakpoints.tabletSmall]: 'row' }}
			rowGap="20px"
			className={styles.dataComplianceWarningWrapper}
		>
			<FlexBlock>
				<FlexBlock alignItems="center" columnGap="12px">
					<div className={styles.iconWrapper}>
						<WarningIcon />
					</div>
					<FlexBlock flexDirection="column">
						<h5>You have trips that might be missing details</h5>
						<div className={styles.message}>
							{hasLast30DaysTrips ? (
								<p>
									You have {tripCount} {tripCount === 1 ? 'trip' : 'trips'} in the{' '}
									{last30DaysPeriodLabel} that {tripCount === 1 ? 'needs' : 'need'} more details
								</p>
							) : (
								<>
									<p>No trips to review from the {last30DaysPeriodLabel}.</p>
									<p>
										You do have {tripCount} {tripCount === 1 ? 'trip' : 'trips'} in your company’s
										annual reporting period that {tripCount === 1 ? 'needs' : 'need'} more details.
									</p>
								</>
							)}
						</div>
					</FlexBlock>
				</FlexBlock>
			</FlexBlock>
			{!isMissingDetailsMode && (
				<button
					onClick={() => {
						navigate(`${routes.tripEmissionLog}?mode=missing-details&period=${reviewPeriod}`);

						if (location.pathname === routes.myClimate) {
							trackEvent(productAnalyticsEvents.myClimate.dataGap.reviewTrips.open, {
								postHogEventName: 'open_data_compliance_travel_log',
								postHogProperties: { category: 'data-compliance' },
							});
							return;
						}

						trackPostHogEvent('open_data_compliance_travel_log', { category: 'data-compliance' });
					}}
				>
					Review {tripCount > 1 ? 'trips' : 'trip'}
				</button>
			)}
		</FlexBlock>
	);
};

export default DataComplianceWarning;
